import { BaseService, axios } from '@/pigeonline/wrapper';
import { app } from '@/main.js';

export default class CustomTopicModelService extends BaseService {
  /**
   * Create new topic model
   * @param {CutomTopicModel} data payload
   *  "name": <str>
      "keywords": <list>,
      "lang": <str>,
      "tags": <list>,
      "source": <dict>
      "optional_fields": <<custom fields>>
    * @returns {CutomTopicModel} 
   */
  async create(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';
      const response = await axios.post(`${URL}`, data);
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:create ' + e.message);
    }
  }

  /**
   * Delete topic model
   * @param {String} id of topic model
   * @returns {String} status message
   */
  async delete(id) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';
      const response = await axios.delete(`${URL}/${id}`);
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:create ' + e.message);
    }
  }

  /**
   * Edit topic model
   * @param {String} id of topic model
   * @param {Object} data topic model object to edit
   * @returns {CutomTopicModel} edited topic model
   */
  async edit(id, data = {}) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';
      const response = await axios.put(`${URL}/${id}`, data);
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:edit ' + e.message);
    }
  }

  /**
   * Get one or more custom topic models
   * @param {String} [id] of topic model
   * @param {Object} [params] filters
   * @returns {(CustomTopicMod|Array)} CustomTopicModel with id or an array
   * of CutomTopicModels based on params or if params and id are not included,
   * fetch all
   */
  async get(id, params = {}) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';
      let config = {};
      config['headers'] = this.getAuthHeader();
      config['params'] = params ? params : '';
      let url = `${URL}${id ? `/${id}` : ''}`;
      const response = await axios.get(url, config);
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:get ' + e.message);
    }
  }

  /**
   * Updates one or more fields of a topic modal
   * @param {String} id of topic model to update
   * @param {Object} data fields to modify
   * @returns {CutomTopicModel} updated topic model
   */
  async update(id, data = {}) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';
      const response = await axios.patch(`${URL}/${id}`, data);
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:update ' + e.message);
    }
  }

  /**
   * Updates one or more fields of a topic modal from an excel file
   */
  async bulk_update(data, project_id, delimiter) {
    data.append('custom_topic_id', project_id);
    data.append('use_delimiter', delimiter);
    const url = `${app.config.globalProperties.$base_URL}/analysis/text/custom_topic_model_bulk_update`;

    const response = await axios.post(url, data, {
      headers: {
        Authorization: this.getAuthHeader(),
      },
      transformRequest: (formData) => formData,
    });

    return response;
  }

  /**
   * Download a version of the topic model
   */

  async download_excel(groupId) {
    try {
      const BASE = app.config.globalProperties.$base_URL + '/analysis/text/custom_topic_model';

      const response = await axios.get(`${BASE}/${groupId}/download`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `custom_topics_${groupId}.xlsx`;
      link.click();

      window.URL.revokeObjectURL(objectUrl);

      return true;
    } catch (e) {
      throw new Error('customTopicModelService.js:download ' + e.message);
    }
  }

  /**
   * Save project themes as learnings for the custom topic model theme
   */
  async saveLearnings(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_save_learnings`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:update ' + e.message);
    }
  }

  /**
   * Check how many learning snapshots exist for this theme
   */
  async learningStatus(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_learning_status`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:update ' + e.message);
    }
  }

  /**
   * Get all snapshots, filterable by this frontend
   */
  async getAllSnapshots(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_get_learnings`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:getAllSnapshots ' + e.message);
    }
  }

  async getSnapshotMetadata(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_get_snapshot_metadata`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:getSnapshotMetadata ' + e.message);
    }
  }

  async getAllSnapshotMetadata(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(
        `${URL}/custom_topic_model_get_all_snapshot_metadata`,
        data,
        {
          headers: this.getAuthHeader(),
        },
      );
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:getAllSnapshotMetadata ' + e.message);
    }
  }

  async dropSnapshot(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_delete_learnings`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:dropSnapshot ' + e.message);
    }
  }

  async trainCustomModel(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_train_model`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('customTopicModelService.js:trainCustomModel ' + e.message);
    }
  }

  async getTrainedModel(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_get_trained_model`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error(
        'customTopicModelService.js:custom_topic_model_get_trained_model ' + e.message,
      );
    }
  }

  async deleteTrainedModel(data) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/custom_topic_model_delete_trained_model`, data, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error(
        'customTopicModelService.js:custom_topic_model_delete_trained_model ' + e.message,
      );
    }
  }
}
